/**
 * iter183 — Leads / Sales Pipeline page.
 *
 * "Preeti-first" UX: overdue follow-ups → today → new leads → proposal
 * status → quick WhatsApp/Call/Follow-up/Open. Minimum typing, maximum
 * automatic activity tracking.
 *
 * Isolated from the existing Bookings/Inquiry flow. Convert-to-Booking
 * calls the leads router which idempotently creates a booking in the
 * existing collection using the existing schema — no duplication.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { inr, fmtDate } from "@/lib/utils";
import {
  Plus, Search, Target, MessageCircle, Phone, Calendar as CalendarIcon,
  ArrowRight, X, Loader2, AlertCircle, Clock3, CheckCircle2, Send,
  FileText, Wallet, Filter, ChevronRight, User as UserIcon, MapPin, Sparkles,
} from "lucide-react";

// ---------------- helpers ----------------
const STAGE_TONES = {
  NEW: "bg-sky-50 text-sky-800 border-sky-200",
  CONTACTED: "bg-indigo-50 text-indigo-800 border-indigo-200",
  QUALIFIED: "bg-violet-50 text-violet-800 border-violet-200",
  PROPOSAL_SENT: "bg-amber-50 text-amber-800 border-amber-200",
  FOLLOW_UP: "bg-orange-50 text-orange-800 border-orange-200",
  NEGOTIATION: "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200",
  VERBAL_YES: "bg-lime-50 text-lime-800 border-lime-200",
  BOOKING_PAYMENT_PENDING: "bg-teal-50 text-teal-800 border-teal-200",
  CONFIRMED: "bg-emerald-50 text-emerald-800 border-emerald-200",
  LOST: "bg-stone-100 text-stone-600 border-stone-200",
};
const BUCKET_TONES = {
  overdue: "bg-rose-50 text-rose-700 border-rose-200",
  today: "bg-amber-50 text-amber-800 border-amber-200",
  upcoming: "bg-emerald-50 text-emerald-800 border-emerald-200",
  none: "bg-stone-50 text-stone-500 border-stone-200",
};

function fmtFollowupTs(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return ts; }
}
function relativeIso(daysAhead = 1, hour = 11, minute = 0) {
  const d = new Date();
  d.setDate(d.getDate() + daysAhead);
  d.setHours(hour, minute, 0, 0);
  // Use local ISO (drop timezone Z) so the value matches human intent.
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(hour)}:${pad(minute)}:00`;
}

// ==================================================================
export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [meta, setMeta] = useState({ stages: [], sources: [], lost_reasons: [] });
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStage, setFilterStage] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [filterFollowup, setFilterFollowup] = useState("any");
  const [showAdd, setShowAdd] = useState(false);
  const [detailLead, setDetailLead] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (filterStage) params.set("stage", filterStage);
      if (filterSource) params.set("source", filterSource);
      if (filterFollowup) params.set("followup", filterFollowup);
      const [listRes, metricsRes] = await Promise.all([
        api.get(`/leads?${params.toString()}`),
        api.get("/leads/metrics"),
      ]);
      setLeads(listRes.data.leads || []);
      setMetrics(metricsRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [search, filterStage, filterSource, filterFollowup]);

  useEffect(() => {
    (async () => {
      try {
        const [c, a] = await Promise.all([
          api.get("/leads/meta/constants"),
          api.get("/leads/assignees"),
        ]);
        setMeta(c.data);
        setAssignees(a.data.assignees || []);
      } catch { /* meta not critical */ }
    })();
  }, []);

  useEffect(() => { load(); }, [load]);

  const refresh = () => load();

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="leads-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-rose-700 font-semibold flex items-center gap-2">
            <Target className="w-3 h-3" strokeWidth={2.4} /> Sales Pipeline
          </div>
          <h1 className="font-heading text-3xl text-stone-900 mt-1">Leads</h1>
          <p className="text-sm text-stone-500 mt-1">
            Overdue & today's follow-ups first. Minimum typing, maximum action.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rose-700 text-white text-sm font-semibold hover:bg-rose-800 shadow-sm"
          data-testid="leads-add-btn"
        >
          <Plus className="w-4 h-4" strokeWidth={2.4} /> New Lead
        </button>
      </div>

      {/* Metrics */}
      <MetricStrip m={metrics} />

      {/* Today's Follow-ups summary */}
      <FollowupSummary m={metrics} onFilter={setFilterFollowup} activeFilter={filterFollowup} />

      {/* Search + filters */}
      <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-3 flex flex-col md:flex-row gap-2 md:items-center">
        <div className="flex-1 flex items-center gap-2 bg-stone-50 rounded-full px-3 py-2 border border-stone-200">
          <Search className="w-4 h-4 text-stone-400" strokeWidth={2} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by couple, phone, location…"
            className="flex-1 bg-transparent outline-none text-sm"
            data-testid="leads-search"
          />
        </div>
        <select value={filterStage} onChange={(e) => setFilterStage(e.target.value)} className="text-xs rounded-full border border-stone-200 px-3 py-2 bg-white" data-testid="leads-filter-stage">
          <option value="">All stages</option>
          {meta.stages.map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
        </select>
        <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)} className="text-xs rounded-full border border-stone-200 px-3 py-2 bg-white" data-testid="leads-filter-source">
          <option value="">All sources</option>
          {meta.sources.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-stone-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading leads…</div>
      ) : leads.length === 0 ? (
        <EmptyState onAdd={() => setShowAdd(true)} />
      ) : (
        <ul className="space-y-2" data-testid="leads-list">
          {leads.map((l) => (
            <LeadRow key={l.id} lead={l} onOpen={() => setDetailLead(l)} onChanged={refresh} />
          ))}
        </ul>
      )}

      {/* Modals */}
      {showAdd && (
        <AddLeadModal
          sources={meta.sources}
          onClose={() => setShowAdd(false)}
          onCreated={() => { setShowAdd(false); refresh(); }}
        />
      )}
      {detailLead && (
        <LeadDetailDrawer
          leadId={detailLead.id}
          meta={meta}
          assignees={assignees}
          onClose={() => setDetailLead(null)}
          onChanged={refresh}
        />
      )}
    </div>
  );
}

// ==================================================================
// Metric strip.
// ==================================================================
function MetricStrip({ m }) {
  const cards = [
    { label: "New Leads", value: m?.new_leads ?? "—", tone: "text-sky-800", testid: "metric-new-leads" },
    { label: "Follow-ups Today", value: m?.followups_today ?? "—", tone: "text-amber-800", testid: "metric-followups-today" },
    { label: "Proposals Sent", value: m?.proposals_sent ?? "—", tone: "text-fuchsia-800", testid: "metric-proposals-sent" },
    { label: "Confirmed", value: m?.confirmed ?? "—", tone: "text-emerald-800", testid: "metric-confirmed" },
    { label: "Lost", value: m?.lost ?? "—", tone: "text-stone-600", testid: "metric-lost" },
    { label: "Conversion", value: m ? `${m.conversion_rate}%` : "—", tone: "text-rose-800", testid: "metric-conversion" },
    { label: "Pipeline Value", value: m ? inr(m.pipeline_total) : "—", tone: "text-teal-800", testid: "metric-pipeline" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="leads-metrics">
      {cards.map((c) => (
        <div key={c.label} className="bg-white border border-stone-200 rounded-xl p-3 shadow-sm" data-testid={c.testid}>
          <div className="text-[9px] uppercase tracking-widest text-stone-400 font-semibold">{c.label}</div>
          <div className={`font-heading text-xl mt-0.5 tabular-nums ${c.tone}`}>{c.value}</div>
        </div>
      ))}
    </div>
  );
}

// ==================================================================
// Follow-up summary — clickable filter row.
// ==================================================================
function FollowupSummary({ m, onFilter, activeFilter }) {
  if (!m) return null;
  const chips = [
    { key: "overdue", label: `${m.followups_overdue} Overdue`, cls: "bg-rose-50 text-rose-700 border-rose-200", dot: "bg-rose-500" },
    { key: "today", label: `${m.followups_today} Due Today`, cls: "bg-amber-50 text-amber-800 border-amber-200", dot: "bg-amber-500" },
    { key: "upcoming", label: `${m.followups_upcoming} Upcoming`, cls: "bg-emerald-50 text-emerald-800 border-emerald-200", dot: "bg-emerald-500" },
    { key: "any", label: "All", cls: "bg-stone-50 text-stone-600 border-stone-200", dot: "bg-stone-400" },
  ];
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="followup-summary">
      <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mr-1">Today's Follow-ups</div>
      {chips.map((c) => (
        <button
          key={c.key}
          type="button"
          onClick={() => onFilter(c.key)}
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${c.cls} ${activeFilter === c.key ? "ring-2 ring-stone-800/20" : "opacity-80 hover:opacity-100"}`}
          data-testid={`followup-chip-${c.key}`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
          {c.label}
        </button>
      ))}
    </div>
  );
}

// ==================================================================
// Lead row + quick actions.
// ==================================================================
function LeadRow({ lead, onOpen, onChanged }) {
  const [showFollowup, setShowFollowup] = useState(false);
  const [showWa, setShowWa] = useState(false);
  const stageTone = STAGE_TONES[lead.stage] || "bg-stone-50 text-stone-600 border-stone-200";
  const bucketTone = BUCKET_TONES[lead.followup_bucket] || BUCKET_TONES.none;

  return (
    <li className="bg-white border border-stone-200 rounded-xl px-4 py-3 flex flex-col md:flex-row md:items-center gap-3 hover:border-stone-300 transition-colors" data-testid={`lead-row-${lead.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] px-2 py-0.5 uppercase tracking-widest font-semibold rounded-full border ${stageTone}`} data-testid={`lead-stage-${lead.id}`}>
            {(lead.stage || "").replaceAll("_", " ")}
          </span>
          {lead.followup_bucket && lead.followup_bucket !== "none" && (
            <span className={`text-[10px] px-2 py-0.5 uppercase tracking-widest font-semibold rounded-full border ${bucketTone}`} data-testid={`lead-bucket-${lead.id}`}>
              {lead.followup_bucket}
            </span>
          )}
          {lead.source && (
            <span className="text-[10px] text-stone-400 uppercase tracking-widest">{lead.source}</span>
          )}
        </div>
        <button
          type="button"
          onClick={onOpen}
          className="font-heading text-lg text-stone-900 hover:text-rose-800 mt-1 text-left block truncate"
          data-testid={`lead-name-${lead.id}`}
        >
          {lead.client_name}
        </button>
        <div className="text-xs text-stone-500 mt-0.5 flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1"><CalendarIcon className="w-3 h-3" /> {fmtDate(lead.wedding_date)}</span>
          {lead.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {lead.location}</span>}
          {lead.budget ? <span className="inline-flex items-center gap-1"><Wallet className="w-3 h-3" /> {inr(lead.budget)}</span> : null}
          {lead.next_followup_at && (
            <span className="inline-flex items-center gap-1"><Clock3 className="w-3 h-3" /> {fmtFollowupTs(lead.next_followup_at)}</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <button
          type="button"
          onClick={() => setShowWa(true)}
          disabled={!lead.wa_me_url}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-600 text-white text-[11px] font-semibold hover:bg-emerald-700 disabled:opacity-40"
          data-testid={`lead-wa-${lead.id}`}
        >
          <MessageCircle className="w-3 h-3" strokeWidth={2.4} /> WhatsApp
        </button>
        <a
          href={lead.tel_url || "#"}
          onClick={(e) => { if (!lead.tel_url) e.preventDefault(); }}
          className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold border ${lead.tel_url ? "border-stone-300 text-stone-700 hover:bg-stone-50" : "border-stone-200 text-stone-300"}`}
          data-testid={`lead-call-${lead.id}`}
        >
          <Phone className="w-3 h-3" strokeWidth={2.4} /> Call
        </a>
        <button
          type="button"
          onClick={() => setShowFollowup(true)}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold border border-stone-300 text-stone-700 hover:bg-stone-50"
          data-testid={`lead-followup-${lead.id}`}
        >
          <Clock3 className="w-3 h-3" strokeWidth={2.4} /> Follow-up
        </button>
        <button
          type="button"
          onClick={onOpen}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-stone-900 text-white text-[11px] font-semibold hover:bg-stone-800"
          data-testid={`lead-open-${lead.id}`}
        >
          Open <ChevronRight className="w-3 h-3" strokeWidth={2.4} />
        </button>
      </div>

      {showFollowup && (
        <FollowupModal lead={lead} onClose={() => setShowFollowup(false)} onSaved={() => { setShowFollowup(false); onChanged(); }} />
      )}
      {showWa && (
        <WhatsappDraftModal lead={lead} onClose={() => setShowWa(false)} onSent={onChanged} />
      )}
    </li>
  );
}

// ==================================================================
// Empty state.
// ==================================================================
function EmptyState({ onAdd }) {
  return (
    <div className="bg-white border border-dashed border-stone-300 rounded-xl p-10 text-center" data-testid="leads-empty">
      <Sparkles className="w-8 h-8 text-rose-400 mx-auto mb-2" strokeWidth={1.8} />
      <div className="font-heading text-lg text-stone-800">No leads yet</div>
      <div className="text-sm text-stone-500 mt-1">Add your first inquiry to start tracking follow-ups.</div>
      <button
        type="button"
        onClick={onAdd}
        className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rose-700 text-white text-sm font-semibold hover:bg-rose-800"
      >
        <Plus className="w-4 h-4" strokeWidth={2.4} /> Add first lead
      </button>
    </div>
  );
}

// ==================================================================
// Add lead — 5 required fields only.
// ==================================================================
function AddLeadModal({ sources, onClose, onCreated }) {
  const [form, setForm] = useState({
    client_name: "", phone: "", wedding_date: "", location: "",
    source: "Instagram", budget: "", requirements: "",
  });
  const [saving, setSaving] = useState(false);
  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.phone || !form.wedding_date || !form.location || !form.source) {
      toast.error("Please fill Client, Phone, Wedding Date, Location, and Source.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.budget) delete payload.budget;
      else payload.budget = Number(payload.budget);
      await api.post("/leads", payload);
      toast.success("Lead created");
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create lead");
    } finally { setSaving(false); }
  };

  return (
    <ModalShell onClose={onClose} title="New Lead" testid="add-lead-modal">
      <form onSubmit={submit} className="space-y-3">
        <Field label="Couple / Client Name *" testid="add-lead-name">
          <input required value={form.client_name} onChange={(e) => setF("client_name", e.target.value)} className="input" placeholder="Anurag & Sanika" data-testid="add-lead-name-input" />
        </Field>
        <Field label="Primary Phone / WhatsApp *" testid="add-lead-phone">
          <input required value={form.phone} onChange={(e) => setF("phone", e.target.value)} className="input" placeholder="+91 98xxx xxxxx" data-testid="add-lead-phone-input" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Wedding Date *">
            <input required type="date" value={form.wedding_date} onChange={(e) => setF("wedding_date", e.target.value)} className="input" data-testid="add-lead-date-input" />
          </Field>
          <Field label="Location *">
            <input required value={form.location} onChange={(e) => setF("location", e.target.value)} className="input" placeholder="Mumbai" data-testid="add-lead-location-input" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Lead Source *">
            <select required value={form.source} onChange={(e) => setF("source", e.target.value)} className="input" data-testid="add-lead-source-input">
              {sources.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Estimated Budget (optional)">
            <input type="number" min="0" value={form.budget} onChange={(e) => setF("budget", e.target.value)} className="input" placeholder="350000" data-testid="add-lead-budget-input" />
          </Field>
        </div>
        <Field label="Requirements / Notes (optional)">
          <textarea rows={2} value={form.requirements} onChange={(e) => setF("requirements", e.target.value)} className="input" placeholder="3-day event, drone, teaser reel…" data-testid="add-lead-notes-input" />
        </Field>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-stone-300 text-stone-700 text-sm hover:bg-stone-50">Cancel</button>
          <button type="submit" disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-rose-700 text-white text-sm font-semibold hover:bg-rose-800 disabled:opacity-60" data-testid="add-lead-submit">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" strokeWidth={2.4} />}
            Create Lead
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ==================================================================
// Follow-up scheduler — chips (Tomorrow / +2d / Next week / Custom).
// ==================================================================
function FollowupModal({ lead, onClose, onSaved }) {
  const [custom, setCustom] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async (isoValue) => {
    setSaving(true);
    try {
      await api.patch(`/leads/${lead.id}`, { next_followup_at: isoValue });
      toast.success(isoValue ? "Follow-up scheduled" : "Follow-up cleared");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to schedule");
    } finally { setSaving(false); }
  };

  const chips = [
    { label: "Tomorrow · 11am", iso: relativeIso(1, 11, 0), testid: "followup-chip-tomorrow" },
    { label: "In 2 days", iso: relativeIso(2, 11, 0), testid: "followup-chip-2d" },
    { label: "Next week", iso: relativeIso(7, 11, 0), testid: "followup-chip-week" },
  ];

  return (
    <ModalShell onClose={onClose} title="Schedule Follow-up" testid="followup-modal">
      <div className="space-y-3">
        <div className="text-sm text-stone-600">for <b className="text-stone-900">{lead.client_name}</b></div>
        <div className="flex flex-wrap gap-2">
          {chips.map((c) => (
            <button key={c.label} type="button" disabled={saving} onClick={() => save(c.iso)} className="px-3 py-2 rounded-full bg-rose-50 text-rose-800 border border-rose-200 text-xs font-semibold hover:bg-rose-100 disabled:opacity-50" data-testid={c.testid}>
              {c.label}
            </button>
          ))}
        </div>
        <div className="pt-2 border-t border-stone-200 space-y-2">
          <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Custom date/time</label>
          <div className="flex gap-2">
            <input type="datetime-local" value={custom} onChange={(e) => setCustom(e.target.value)} className="input flex-1" data-testid="followup-custom-input" />
            <button type="button" disabled={saving || !custom} onClick={() => save(custom + ":00")} className="px-4 py-2 rounded-full bg-stone-900 text-white text-xs font-semibold hover:bg-stone-800 disabled:opacity-40" data-testid="followup-custom-save">
              Save
            </button>
          </div>
        </div>
        {lead.next_followup_at && (
          <button type="button" disabled={saving} onClick={() => save(null)} className="text-xs text-rose-600 hover:text-rose-800 font-semibold uppercase tracking-widest" data-testid="followup-clear">
            Clear existing follow-up
          </button>
        )}
      </div>
    </ModalShell>
  );
}

// ==================================================================
// WhatsApp draft modal.
// ==================================================================
function WhatsappDraftModal({ lead, onClose, onSent }) {
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/leads/${lead.id}/wa-draft`);
        setText(data.text);
        setUrl(data.wa_me_url);
      } catch { toast.error("Couldn't load draft"); }
      finally { setLoading(false); }
    })();
  }, [lead.id]);

  const sendAndLog = async () => {
    if (!url) return;
    try {
      window.open(`${url}${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
      await api.post(`/leads/${lead.id}/activity`, { message: "WhatsApp follow-up sent" });
      toast.success("Opened WhatsApp · activity logged");
      onSent && onSent();
      onClose();
    } catch (err) {
      toast.error("Couldn't log activity");
    }
  };

  return (
    <ModalShell onClose={onClose} title="WhatsApp Follow-up" testid="wa-modal">
      {loading ? (
        <div className="text-sm text-stone-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Preparing draft…</div>
      ) : (
        <div className="space-y-3">
          <div className="text-xs text-stone-500">Message to <b className="text-stone-900">{lead.client_name}</b>. Edit before sending — nothing is sent automatically.</div>
          <textarea rows={7} value={text} onChange={(e) => setText(e.target.value)} className="input font-mono text-sm !bg-stone-50" data-testid="wa-message-textarea" />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-stone-300 text-stone-700 text-sm hover:bg-stone-50">Cancel</button>
            <button type="button" disabled={!url || !text.trim()} onClick={sendAndLog} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50" data-testid="wa-send-btn">
              <Send className="w-3.5 h-3.5" strokeWidth={2.4} /> Open WhatsApp
            </button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

// ==================================================================
// Lead detail drawer.
// ==================================================================
function LeadDetailDrawer({ leadId, meta, assignees, onClose, onChanged }) {
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [showFollowup, setShowFollowup] = useState(false);
  const [showWa, setShowWa] = useState(false);
  const [showLost, setShowLost] = useState(false);
  const [showConvert, setShowConvert] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/leads/${leadId}`);
      setLead(data);
    } catch { toast.error("Lead not found"); onClose(); }
    finally { setLoading(false); }
  }, [leadId, onClose]);
  useEffect(() => { load(); }, [load]);

  const patch = async (payload) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/leads/${leadId}`, payload);
      setLead(data);
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      await api.post(`/leads/${leadId}/activity`, { message: note.trim() });
      setNote("");
      await load();
      onChanged && onChanged();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose} data-testid="lead-detail-drawer">
      <div onClick={(e) => e.stopPropagation()} className="bg-white h-full w-full max-w-xl overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-stone-200 p-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-rose-700" strokeWidth={2.4} />
            <div className="font-heading text-lg text-stone-900" data-testid="lead-detail-name">
              {lead?.client_name || "Lead"}
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-stone-500 hover:text-stone-900" data-testid="lead-detail-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading || !lead ? (
          <div className="p-6 text-stone-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Info label="Wedding Date" value={fmtDate(lead.wedding_date)} />
              <Info label="Location" value={lead.location} />
              <Info label="Source" value={lead.source} />
              <Info label="Budget" value={lead.budget ? inr(lead.budget) : "—"} />
              <Info label="Next Follow-up" value={fmtFollowupTs(lead.next_followup_at)} />
              <Info label="Assigned To" value={lead.assignee_name || "—"} />
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={lead.stage}
                onChange={(e) => patch({ stage: e.target.value })}
                disabled={saving}
                className="text-xs rounded-full border border-stone-300 px-3 py-1.5 bg-white"
                data-testid="lead-detail-stage-select"
              >
                {meta.stages.map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
              </select>
              <select
                value={lead.assigned_to || ""}
                onChange={(e) => patch({ assigned_to: e.target.value || null })}
                disabled={saving}
                className="text-xs rounded-full border border-stone-300 px-3 py-1.5 bg-white"
                data-testid="lead-detail-assign-select"
              >
                <option value="">Unassigned</option>
                {assignees.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button type="button" disabled={!lead.wa_me_url} onClick={() => setShowWa(true)} className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-40" data-testid="lead-detail-wa">
                <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
              </button>
              <a href={lead.tel_url || "#"} onClick={(e) => { if (!lead.tel_url) e.preventDefault(); }} className={`inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full text-xs font-semibold border ${lead.tel_url ? "border-stone-300 text-stone-700 hover:bg-stone-50" : "border-stone-200 text-stone-300"}`} data-testid="lead-detail-call">
                <Phone className="w-3.5 h-3.5" /> Call
              </a>
              <button type="button" onClick={() => setShowFollowup(true)} className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full border border-stone-300 text-xs font-semibold text-stone-700 hover:bg-stone-50" data-testid="lead-detail-followup">
                <Clock3 className="w-3.5 h-3.5" /> Schedule
              </button>
              <button type="button" onClick={() => setShowConvert(true)} disabled={saving || lead.stage === "LOST"} className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full bg-stone-900 text-white text-xs font-semibold hover:bg-stone-800 disabled:opacity-40" data-testid="lead-detail-convert">
                <ArrowRight className="w-3.5 h-3.5" /> Convert to Booking
              </button>
            </div>

            {lead.booking_id && (
              <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2" data-testid="lead-detail-linked-booking">
                <b>Linked booking:</b> {lead.booking_id}
              </div>
            )}

            {/* Add note */}
            <div className="border border-stone-200 rounded-lg p-3 space-y-2 bg-stone-50">
              <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Add manual note</div>
              <div className="flex gap-2">
                <input value={note} onChange={(e) => setNote(e.target.value)} className="input flex-1" placeholder="e.g. Called, no answer, will retry Wed" data-testid="lead-detail-note-input" />
                <button type="button" disabled={saving || !note.trim()} onClick={addNote} className="px-3 py-2 rounded-full bg-stone-900 text-white text-xs font-semibold disabled:opacity-40" data-testid="lead-detail-note-save">Add</button>
              </div>
            </div>

            {/* Activity timeline */}
            <ActivityTimeline entries={lead.activity_timeline || []} />

            {/* Mark Lost */}
            {lead.stage !== "LOST" && (
              <button type="button" onClick={() => setShowLost(true)} className="text-xs text-stone-500 hover:text-rose-700 underline underline-offset-4 decoration-dotted" data-testid="lead-detail-mark-lost">
                Mark as Lost
              </button>
            )}

            {showFollowup && <FollowupModal lead={lead} onClose={() => setShowFollowup(false)} onSaved={() => { setShowFollowup(false); load(); onChanged && onChanged(); }} />}
            {showWa && <WhatsappDraftModal lead={lead} onClose={() => setShowWa(false)} onSent={load} />}
            {showLost && <LostReasonModal lead={lead} reasons={meta.lost_reasons} onClose={() => setShowLost(false)} onSaved={() => { setShowLost(false); load(); onChanged && onChanged(); }} />}
            {showConvert && <ConvertModal lead={lead} onClose={() => setShowConvert(false)} onDone={() => { setShowConvert(false); load(); onChanged && onChanged(); }} />}
          </div>
        )}
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-stone-400 font-semibold">{label}</div>
      <div className="text-sm text-stone-900 mt-0.5">{value}</div>
    </div>
  );
}

function ActivityTimeline({ entries }) {
  if (!entries || entries.length === 0) {
    return <div className="text-xs text-stone-400 italic">No activity yet.</div>;
  }
  const sorted = [...entries].sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  return (
    <div data-testid="lead-detail-activity">
      <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold mb-2">Activity ({sorted.length})</div>
      <ul className="space-y-2">
        {sorted.map((a) => (
          <li key={a.id} className="flex items-start gap-2 text-sm">
            <div className="w-1.5 h-1.5 rounded-full bg-rose-500 mt-1.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-stone-800">{a.message}</div>
              <div className="text-[10px] text-stone-400 mt-0.5">{fmtFollowupTs(a.at)} · {a.by || "system"}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LostReasonModal({ lead, reasons, onClose, onSaved }) {
  const [reason, setReason] = useState(reasons[0] || "Other");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/leads/${lead.id}`, { lost_reason: reason, lost_note: note });
      toast.success("Lead marked lost");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };
  return (
    <ModalShell onClose={onClose} title="Mark Lead as Lost" testid="lost-modal">
      <div className="space-y-3">
        <Field label="Reason">
          <select value={reason} onChange={(e) => setReason(e.target.value)} className="input" data-testid="lost-reason-select">
            {reasons.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Note (optional)">
          <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="input" data-testid="lost-note-input" />
        </Field>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-stone-300 text-sm">Cancel</button>
          <button type="button" onClick={save} disabled={saving} className="px-4 py-2 rounded-full bg-rose-700 text-white text-sm font-semibold hover:bg-rose-800 disabled:opacity-60" data-testid="lost-confirm-btn">Mark Lost</button>
        </div>
      </div>
    </ModalShell>
  );
}

function ConvertModal({ lead, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const convert = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/convert`);
      setResult(data);
      if (data.created) toast.success("Booking created");
      else toast.success("Existing booking linked");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Convert failed");
    } finally { setBusy(false); }
  };
  return (
    <ModalShell onClose={onClose} title="Convert to Booking" testid="convert-modal">
      {result ? (
        <div className="space-y-3">
          <div className="text-sm text-stone-700">
            {result.created ? "New booking created:" : "Existing booking linked:"}
          </div>
          <div className="font-mono text-xs bg-stone-100 rounded p-2 border border-stone-200" data-testid="convert-result-booking-id">{result.booking_id}</div>
          <div className="text-xs text-stone-500">The booking will now appear in the existing Bookings page in status <b>CONFIRM</b>. Payments, invoices and the Client Tracker will continue through the existing flow.</div>
          <button type="button" onClick={onDone} className="px-4 py-2 rounded-full bg-stone-900 text-white text-sm font-semibold hover:bg-stone-800">Done</button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="text-sm text-stone-700">
            This will {lead.booking_id ? "open the existing linked booking" : "create a new booking"} using the following lead information:
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs bg-stone-50 border border-stone-200 rounded-md p-3">
            <div><b>Couple:</b> {lead.client_name}</div>
            <div><b>Phone:</b> {lead.phone}</div>
            <div><b>Date:</b> {fmtDate(lead.wedding_date)}</div>
            <div><b>Location:</b> {lead.location}</div>
            {lead.budget ? <div className="col-span-2"><b>Amount:</b> {inr(lead.budget)}</div> : null}
          </div>
          <div className="text-[11px] text-stone-500 italic">Conversion is idempotent — clicking twice won't create a duplicate booking.</div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-stone-300 text-sm">Cancel</button>
            <button type="button" disabled={busy} onClick={convert} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-stone-900 text-white text-sm font-semibold hover:bg-stone-800 disabled:opacity-60" data-testid="convert-confirm-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              {lead.booking_id ? "Open Existing" : "Create Booking"}
            </button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

// ==================================================================
// Small primitives.
// ==================================================================
function ModalShell({ onClose, title, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid={testid}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl w-full max-w-lg max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="px-5 py-3 border-b border-stone-200 flex items-center justify-between sticky top-0 bg-white">
          <div className="font-heading text-lg text-stone-900">{title}</div>
          <button type="button" onClick={onClose} className="text-stone-500 hover:text-stone-900 p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-widest text-stone-500 font-semibold mb-1">{label}</span>
      {children}
    </label>
  );
}
